// CustomEdge.tsx
import type { EdgeProps } from '@xyflow/react';
import { useState, useLayoutEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { useTheme } from '@mui/material';
import { BaseEdge, EdgeLabelRenderer, getBezierPath, useReactFlow } from '@xyflow/react';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { useStore } from '@stores/RootStore';
import { PlusIcon } from '@icons/PlusIcon';
import { WorkflowActionDTOModel } from '@repositories/workflowRepository/models/WorkflowActionDTOModel';
import { SelectNodeSpeech } from './SelectNodeSpeech';

export type SpeechType = 'ACTION' | 'CONDITION';

export const NormalEdge = observer(
  ({
    source,
    target,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    style = {},
    markerEnd,
  }: EdgeProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { workflowDetailStore } = useStore();
    const isEditable = workflowDetailStore.checkEditable();

    // source 노드가 조건 노드인지 확인
    const sourceNode = workflowDetailStore.findNode(source);
    const isFromCondition = sourceNode?.type === 'condition';

    const [isHover, setIsHover] = useState<boolean>(false);
    const [showMenu, setShowMenu] = useState<boolean>(false);
    const { getViewport, fitView } = useReactFlow();
    const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });

    /**
     * 노드 추가 후 뷰 조정 (전체 보기로 이동)
     * 이중 requestAnimationFrame으로 DOM 업데이트 완료 후 실행
     */
    const adjustViewAfterNodeAdd = useCallback(() => {
      // 이중 rAF: 브라우저가 레이아웃 계산을 완료한 후 fitView 실행
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          fitView({ padding: 0.2, duration: 500 });
        });
      });
    }, [fitView]);

    /**
     * 엣지 경로 계산
     */
    const [edgePath, labelX, labelY] = getBezierPath({
      sourceX,
      sourceY,
      sourcePosition,
      targetX,
      targetY,
      targetPosition,
    });

    /**
     * 버튼 위치 계산
     */
    const midX = (sourceX + targetX) / 2;
    const midY = (sourceY + targetY) / 2;

    /**
     * 메뉴 위치 계산 (viewport transform 고려)
     */
    useLayoutEffect(() => {
      if (showMenu) {
        const viewport = getViewport();
        const reactFlowContainer = workflowDetailStore.reactFlowContainerRef;
        const reactFlowBounds = reactFlowContainer?.getBoundingClientRect();

        if (reactFlowBounds) {
          const x = reactFlowBounds.left + midX * viewport.zoom + viewport.x - 136;
          const y = reactFlowBounds.top + midY * viewport.zoom + viewport.y + 30;
          setMenuPosition({ x, y });
        }
      }
    }, [showMenu, midX, midY, getViewport, workflowDetailStore.reactFlowContainerRef]);

    /**
     * 더하기 버튼 클릭 - 메뉴 표시
     */
    const handleClickAddBtn = (e: React.MouseEvent) => {
      e.stopPropagation();
      setShowMenu(prev => !prev);
    };

    const handleClickSpeech = (e: React.MouseEvent, type: SpeechType) => {
      e.stopPropagation();
      if (type === 'ACTION') {
        const newActionNode = workflowDetailStore.addActionNode(source, undefined, target);
        workflowDetailStore.curNodeId = newActionNode.id;
        workflowDetailStore.setToolbarView('actionSelect');
        adjustViewAfterNodeAdd();
      } else if (type === 'CONDITION') {
        // 조건 분기 액션 모델 생성
        const conditionActionModel = new WorkflowActionDTOModel({
          type: 'IF_ACTION',
          actionName: t('Workflow_Create_IfAction'),
          actionMethod: '',
          actionArguments: [
            {
              argumentName: 'expr',
              expression: '',
              expressionType: 'SIMPLE',
            },
          ],
        });

        // 조건 노드를 직접 추가
        const newConditionNode = workflowDetailStore.addConditionNode(source, conditionActionModel, target);

        runInAction(() => {
          workflowDetailStore.curNodeId = newConditionNode.id;
          workflowDetailStore.setToolbarView('conditionSelect');
        });
        adjustViewAfterNodeAdd();
      }

      setShowMenu(false);
    };

    return (
      <>
        <BaseEdge path={edgePath} markerEnd={markerEnd} style={style} />
        <EdgeLabelRenderer>
          {/* 조건 노드에서 나가는 경우 'false' 라벨 표시 (Add 버튼 위에 배치) */}
          {isFromCondition && (
            <FalseLabel
              style={{
                position: 'absolute',
                transform: `translate(-50%, -50%) translate(${labelX}px,${labelY - 30}px)`,
                pointerEvents: 'all',
              }}
            >
              false
            </FalseLabel>
          )}
          {/* '+' 버튼 - EdgeLabelRenderer로 SVG 밖에서 렌더링하여 항상 최상단에 배치 */}
          {isEditable && (
            <Add
              isActive={showMenu}
              onClick={handleClickAddBtn}
              onMouseEnter={() => setIsHover(true)}
              onMouseLeave={() => setIsHover(false)}
              style={{
                position: 'absolute',
                transform: `translate(-50%, -50%) translate(${midX}px,${midY}px)`,
              }}
            >
              <PlusIcon color={showMenu || isHover ? theme.colors['ic-primary'] : theme.colors['ic-gray-main']} />
            </Add>
          )}
        </EdgeLabelRenderer>
        {/* 액션/조건 선택 메뉴 - Portal로 렌더링 */}
        {isEditable && showMenu && <SelectNodeSpeech menuPosition={menuPosition} onClickSpeech={handleClickSpeech} />}
      </>
    );
  },
);

const FalseLabel = styled.div`
  display: flex;
  padding: 3px 12px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 12px;
  border: 1px solid ${props => props.theme.colors['border-gray-lighter']};
  background: ${props => props.theme.colors['bg-white']};
  font-size: 12px;
  height: fit-content;
  line-height: 1;
  pointer-events: all;
  width: fit-content;
  white-space: nowrap;
`;

const Add = styled.div<{ isActive?: boolean }>`
  display: flex;
  flex-direction: row;
  width: 24px;
  height: 24px;
  align-items: center;
  border: ${props => (props.isActive ? '2px' : '1px')} solid
    ${props => (props.isActive ? props.theme.colors['border-primary'] : props.theme.colors['border-gray-dark'])};
  border-radius: 3px;
  background: ${props => (props.isActive ? props.theme.colors['bg-primary-lighter'] : props.theme.colors['bg-white'])};
  justify-content: center;
  box-shadow: 0 0 3px 0 rgba(0, 0, 0, 0.07), 0 1px 0 0 rgba(0, 0, 0, 0.02);
  cursor: pointer;
  pointer-events: all;
  :hover {
    border: 1px solid ${props => props.theme.colors['border-primary']};
    background-color: ${props => props.theme.colors['bg-primary-light-50']};
  }
`;
